/**
 * 9:16 share card — tier + counts only. NEVER lists names (privacy).
 */
import { store } from '../store.ts';
import { TIER_DISPLAY } from '../types.ts';
import type { Summary } from '../types.ts';

export class DialShareCard extends HTMLElement {
  #shadow = this.attachShadow({ mode: 'open' });
  #unsub?: () => void;
  #current: Summary | null = null;

  connectedCallback(): void {
    this.#unsub = store.subscribe((s) => {
      this.#current = s.summary;
      this.#render();
    });
  }
  disconnectedCallback(): void { this.#unsub?.(); }

  #render(): void {
    this.#shadow.innerHTML = /* html */ `
<style>
:host {
  display: grid;
  gap: 10px;
  view-transition-name: share-card;
}
button {
  font-family: var(--mono);
  font-size: 11px;
  letter-spacing: 0.32em;
  text-transform: uppercase;
  padding: 16px;
  background: linear-gradient(180deg, var(--maroon), var(--maroon-deep));
  color: var(--paper);
  border-radius: 8px;
  font-weight: 700;
  transition: transform 180ms cubic-bezier(0.34, 1.56, 0.64, 1), filter 120ms;
  border: 1px solid color-mix(in oklch, var(--maroon-deep) 60%, transparent);
}
button:hover { transform: translateY(-1px); }
button:active { filter: brightness(0.92); transform: translateY(0); }
button.ghost {
  background: transparent;
  color: var(--maroon);
  border: 1px solid var(--rule);
}
button.ghost:hover {
  background: color-mix(in oklch, var(--maroon) 8%, transparent);
  border-color: var(--maroon);
}
.note {
  font-family: var(--mono);
  font-size: 9px;
  color: var(--ink-faint);
  letter-spacing: 0.18em;
  text-align: center;
  margin-top: 4px;
  text-wrap: pretty;
}
</style>
<button id="share">Share kahaani card</button>
<button class="ghost" id="download">Download 9:16 PNG</button>
<div class="note">share card shows tier + counts only. names never leave your device.</div>
`;
    this.#shadow.getElementById('share')?.addEventListener('click', () => this.#share());
    this.#shadow.getElementById('download')?.addEventListener('click', () => this.#download());
  }

  async #renderCanvas(): Promise<Blob | null> {
    if (!this.#current) return null;
    const W = 1080;
    const H = 1920;
    const canvas = new OffscreenCanvas(W, H);
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    const s = this.#current;
    const meta = TIER_DISPLAY[s.tier];

    ctx.fillStyle = '#f7f0e2';
    ctx.fillRect(0, 0, W, H);

    const grad = ctx.createRadialGradient(W / 2, 700, 60, W / 2, 700, 900);
    grad.addColorStop(0, 'rgba(228, 174, 116, 0.40)');
    grad.addColorStop(1, 'rgba(247, 240, 226, 0)');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, W, H);

    ctx.fillStyle = '#5a1a23';
    ctx.font = '700 64px "Cormorant Garamond", serif';
    ctx.textAlign = 'left';
    ctx.fillText('डायल', 80, 200);

    ctx.fillStyle = 'rgba(90, 26, 35, 0.5)';
    ctx.font = '500 22px "Space Mono", monospace';
    ctx.fillText('DIAL · MY INNER CIRCLE', 80, 240);

    ctx.fillStyle = '#a4502c';
    ctx.font = '500 32px "Space Mono", monospace';
    ctx.fillText('TIER', 80, 420);
    ctx.fillStyle = '#5a1a23';
    ctx.font = 'italic 700 96px "Cormorant Garamond", serif';
    ctx.fillText(meta.name, 80, 520);
    ctx.fillStyle = 'rgba(90, 26, 35, 0.75)';
    ctx.font = 'italic 500 36px "Cormorant Garamond", serif';
    ctx.fillText(`"${meta.tagline}"`, 80, 580);

    ctx.fillStyle = 'rgba(90, 26, 35, 0.55)';
    ctx.font = '500 24px "Space Mono", monospace';
    ctx.fillText('ACTIVE CONNECTIONS', 80, 760);
    ctx.fillStyle = '#5a1a23';
    ctx.font = '700 200px "Cormorant Garamond", serif';
    ctx.fillText(String(s.active_connection_count), 80, 940);

    ctx.fillStyle = 'rgba(90, 26, 35, 0.55)';
    ctx.font = '500 24px "Space Mono", monospace';
    ctx.fillText('CITIES COVERED', 80, 1080);
    ctx.fillStyle = '#5a1a23';
    ctx.font = '700 56px "Cormorant Garamond", serif';
    ctx.fillText(s.insights.cities_covered.slice(0, 4).join(' · ') || '—', 80, 1160);

    ctx.fillStyle = 'rgba(90, 26, 35, 0.55)';
    ctx.font = '500 24px "Space Mono", monospace';
    ctx.fillText('CIRCLE WARMTH (AVG)', 80, 1320);
    ctx.fillStyle = '#5a1a23';
    ctx.font = '700 88px "Cormorant Garamond", serif';
    ctx.fillText(Math.round(s.warmth_total).toString(), 80, 1420);

    ctx.fillStyle = 'rgba(90, 26, 35, 0.4)';
    ctx.font = '500 20px "Space Mono", monospace';
    ctx.fillText('dial.yourtube.in', 80, H - 100);
    ctx.fillStyle = '#a4502c';
    ctx.fillText('▣', W - 110, H - 100);

    return await canvas.convertToBlob({ type: 'image/png' });
  }

  async #share(): Promise<void> {
    const blob = await this.#renderCanvas();
    if (!blob) return;
    const file = new File([blob], 'dial-kahaani.png', { type: 'image/png' });
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({ files: [file], title: 'My Dial', text: 'My inner circle kahaani' });
      } catch {
        this.#download(blob);
      }
    } else {
      this.#download(blob);
    }
  }

  async #download(b?: Blob): Promise<void> {
    const blob = b ?? await this.#renderCanvas();
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'dial-kahaani.png';
    a.click();
    URL.revokeObjectURL(url);
  }
}

customElements.define('dial-share-card', DialShareCard);
